// The one way to the api: openapi-fetch typed from the committed OpenAPI
// (src/api/schema.d.ts, `npm run api:types`). No hand-written response types.
//
// Two middlewares: every request carries a fresh X-Request-ID (the api echoes
// it and logs it) and, with authMode "dev", the X-Dev-User of the configured
// user; every non-2xx answer becomes an ApiError carrying the problem+json body.
// With authMode "entra" there is no header: the session is the api's cookie
// (credentials "same-origin"), and a 401 takes the whole window to the login,
// once (see signIn below).
import createClient, { type Middleware } from "openapi-fetch";

import { runtimeConfig, type RuntimeConfig } from "../config/runtime";
import type { paths } from "./schema";

/** application/problem+json, as Loggione renders it. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string | null;
  instance?: string;
  request_id?: string | null;
  [extra: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  readonly requestId: string | null;

  constructor(status: number, problem: Problem | null, requestId: string | null) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.requestId = requestId;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function newRequestId(): string {
  // crypto.randomUUID exists in a secure context only (https, or localhost):
  // a page reached by its container's name has none, getRandomValues always
  const c = globalThis.crypto;
  if (typeof c.randomUUID === "function") return `platea-${c.randomUUID()}`;
  const bytes = c.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  return `platea-${uuid}`;
}

/** The login of Loggione's BFF, when a 401 does not say where it is. */
export const DEFAULT_LOGIN_URL = "/api/v1/auth/login";

/** The window's navigation, in one place so a test can watch it (jsdom cannot navigate). */
export const windowNavigation = {
  assign(url: string): void {
    window.location.assign(url);
  },
};

let signingIn = false;

/** The login address with the page to come back to: the current path and query, URL-encoded. */
export function loginAddress(loginUrl: string, location: { pathname: string; search: string }): string {
  return `${loginUrl}?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
}

/** A login_url is an internal path (one leading slash): anything else is not followed. */
function internalPath(value: unknown): string {
  return typeof value === "string" && /^\/(?![/\\])/.test(value) ? value : DEFAULT_LOGIN_URL;
}

/**
 * Takes the whole window to the login, with the current location as `next`.
 * Only the first call navigates: while the window is leaving, the 401s of the
 * calls still in flight must not start it again.
 */
export function signIn(loginUrl?: unknown): void {
  if (signingIn) return;
  signingIn = true;
  windowNavigation.assign(loginAddress(internalPath(loginUrl), window.location));
}

const sameOrigin: { credentials: RequestCredentials } = { credentials: "same-origin" };

export function createApi(config: RuntimeConfig = runtimeConfig()) {
  const client = createClient<paths>({
    baseUrl: config.apiBase,
    // the session of entra is a cookie, of the page's own origin
    ...(config.authMode === "entra" ? sameOrigin : {}),
  });
  const identity: Middleware = {
    onRequest({ request }) {
      request.headers.set("X-Request-ID", newRequestId());
      if (config.authMode === "dev") request.headers.set("X-Dev-User", config.devUser);
      return request;
    },
    async onResponse({ response }) {
      if (response.ok) return response;
      let problem: Problem | null = null;
      if (response.headers.get("content-type")?.includes("json")) {
        try {
          problem = (await response.clone().json()) as Problem;
        } catch {
          problem = null;
        }
      }
      if (response.status === 401 && config.authMode === "entra") signIn(problem?.login_url);
      throw new ApiError(response.status, problem, response.headers.get("x-request-id"));
    },
  };
  client.use(identity);
  return client;
}

export type Api = ReturnType<typeof createApi>;

/** The app's client. Built on first use, so tests can set config.js first. */
let instance: Api | undefined;
export function api(): Api {
  instance ??= createApi();
  return instance;
}

/** For tests: forget the app's client (a new config.js, a new client). */
export function resetApi(): void {
  instance = undefined;
  signingIn = false;
}

/** openapi-fetch leaves `data` undefined on an empty body; an answer we expect a body from never is. */
export function unwrap<T>(data: T | undefined): T {
  if (data === undefined) throw new Error("empty answer where a body was expected");
  return data;
}
